import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Alert, Button, Card, Stack, Typography } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useOverlay, { OverlayState } from '@/hooks/useOverlay'
import { useCreateOrgCredential } from '@/service-library/hooks/org-credentials'
import { useUpdateOrganization } from '@/service-library/hooks/organizations'
import queryKeys from '@/service-library/query-keys'
import { getCredential } from '@/services/credentials'
import useAllOrganizations from '@/services/hooks/useAllOrganizations'
import { OrgCredential, OrgCredentialType } from '@/types/credentials'
import { Organization } from '@/types/organizations'
import { orgCredentialTypeHasEditableAuthType } from '@/utils/org-credentials'
import {
  showErrorSnackbar,
  showInfoSnackbar,
  showSuccessSnackbar,
} from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { PixyDocsForm } from '@/components/forms'
import OrganizationPickerWithDialog from '@/components/organization-select/OrganizationPickerWithDialog'
import ManageCredentialAuthDialog from './ManageCredentialAuthDialog'

function DialogAuthTypeText({ type }: { type: OrgCredentialType }) {
  if (type.auth_type.includes('app_xchange')) {
    return (
      <Typography>
        After saving, navigate to AppXchange to complete your authentication
        setup.
      </Typography>
    )
  }
  return (
    <div>
      <Typography>
        After clicking &quot;Save&quot;, you will be prompted to:
      </Typography>
      <ul>
        {type.auth_type.includes('token') && (
          <li>Enter an API token from {type.name}</li>
        )}
        {type.auth_type.includes('secret') && (
          <li>Enter a Client ID and Secret</li>
        )}
        {type.auth_type.includes('oauth2') && <li>Sign in to {type.name}</li>}
        {type.auth_type.includes('basic_with_url') && (
          <>
            <li>Enter your API url from {type.name}</li>
            <li>Enter your username and password</li>
          </>
        )}
      </ul>
    </div>
  )
}

type FormValues = {
  org: Organization
  name: string
}

type AddCredentialDialogProps = {
  overlay: OverlayState
  orgCredentials: OrgCredential[]
  listQueryKey: QueryKey
  type: OrgCredentialType
}

export default function AddCredentialDialog({
  overlay,
  orgCredentials,
  listQueryKey,
  type,
}: AddCredentialDialogProps) {
  const { rootOrganization } = useCurrentAccountContext()

  const hasEditableAuthType = orgCredentialTypeHasEditableAuthType(type)
  const manageAuthOverlay = useOverlay()

  const enableFetchingRef = useRef(false)
  const { organizations, isFetchingAll: isFetchingOrgs } = useAllOrganizations({
    filters: {
      self_and_descendants_for_id: rootOrganization.id,
    },
    enabled: enableFetchingRef.current, // We don't want to make a call every time the dialog opens, just the first time
  })

  const [newCredential, setNewCredential] = useState<OrgCredential | undefined>(
    undefined,
  )

  const defaultValues = useMemo(
    () => ({
      org: rootOrganization,
    }),
    [rootOrganization],
  )

  const methods = useForm<FormValues>({
    defaultValues,
  })

  const { formState, reset, setValue, watch } = methods

  function openAuthSignIn(credential: OrgCredential) {
    showInfoSnackbar(`Opening ${credential.name} Sign-In...`)
    getCredential(type.trigger_url, credential.id).then(({ data }) => {
      window.open(data.auth_url, '_blank')
    })
  }

  const { updateOrganization } = useUpdateOrganization({
    sideEffectQueryKeys: [queryKeys.organizations.lists()],
  })

  const { createOrgCredential } = useCreateOrgCredential({
    listQueryKey,
    sideEffectQueryKeys: [queryKeys.orgCredentials.all],
    onSuccess: (newCred) => {
      if (type.supports.companies !== 'required') {
        if (newCred.org && !newCred.org.is_relay_data_provider) {
          updateOrganization({
            ...newCred.org,
            is_relay_data_provider: true,
          })
        }
      }

      setNewCredential(newCred)
      if (hasEditableAuthType) {
        manageAuthOverlay.open()
      } else if (type.auth_type.includes('oauth2')) {
        openAuthSignIn(newCred)
      } else {
        showSuccessSnackbar('Credential Added')
      }
    },
    onError: () => {
      showErrorSnackbar('Failed to create credential. Please contact support.')
    },
  })

  function handleCreateCredential(values: FormValues) {
    createOrgCredential({
      id: generateUuid(),
      name: type.name,
      org_id: values.org.id,
      org: values.org,
      type_id: type.id,
      type,
      needs_auth: type.auth_type.includes('oauth2'),
      needs_secrets: hasEditableAuthType,
      roll_up_line_items_default: true,
    })

    overlay.close()
  }

  const { isValid } = formState
  const org = watch('org')

  const existingCredential = useMemo(() => {
    if (org.id) {
      return orgCredentials.find(
        (credential) =>
          credential.type_id === type.id && credential.org_id === org.id,
      )
    }
    return
  }, [orgCredentials, org.id, type.id])

  useEffect(() => {
    if (overlay.isOpen) {
      reset(defaultValues)
    }
  }, [defaultValues, overlay.isOpen, reset])

  useEffect(() => {
    if (overlay.isOpen && !enableFetchingRef.current) {
      enableFetchingRef.current = true
    }
  }, [overlay.isOpen])

  // A credential type can have multiple auth types, but we only want to show one in the "Set/Edit" button,
  // so we just use the first one we find. Credential types should not have a combination of any of these three.
  const newCredentialAuthSetType = type?.auth_type.find((authType) => {
    return (
      authType === 'secret' ||
      authType === 'token' ||
      authType === 'basic_with_url'
    )
  })

  return (
    <>
      <Dialog title={`New ${type.name} Credential`} {...overlay} maxWidth="xs">
        {existingCredential && (
          <Alert severity="warning" sx={{ mx: 3 }}>
            You already have a {existingCredential.name} credential for this
            organization.
          </Alert>
        )}
        <PixyDocsForm methods={methods} onSubmit={handleCreateCredential}>
          <DialogContent>
            <Stack spacing={3}>
              <Card>
                <OrganizationPickerWithDialog
                  currentOrganization={watch('org')}
                  organizations={organizations}
                  rootTreeOrgId={rootOrganization.id}
                  isFetching={isFetchingOrgs}
                  onSave={(organization) => {
                    setValue('org', organization, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }}
                />
              </Card>
              <DialogAuthTypeText type={type} />
            </Stack>
          </DialogContent>
          <DialogFooter>
            <Button variant="text" onClick={overlay.close}>
              Cancel
            </Button>
            <Button disabled={!!existingCredential || !isValid} type="submit">
              Save
            </Button>
          </DialogFooter>
        </PixyDocsForm>
      </Dialog>

      {newCredential && newCredentialAuthSetType && (
        <ManageCredentialAuthDialog
          authType={newCredentialAuthSetType}
          isEdit={!newCredential.needs_secrets}
          overlay={manageAuthOverlay}
          orgCredential={newCredential}
          type={type}
          onSave={() => {
            if (type.auth_type.includes('oauth2')) {
              openAuthSignIn(newCredential)
            }
            setNewCredential(undefined)
          }}
        />
      )}
    </>
  )
}
